import type { FieldType, IQuoteState } from '@/interfaces'

import { addToJSON } from './helper'

export class QuoteState implements IQuoteState {
	[key: string]: FieldType

	// New API structure
	id: number
	name: string
	description: string
	color: string
	icon: string
	isInitial: boolean
	isFinal: boolean
	allowEdit: boolean
	editBehavior: 'direct' | 'reset_to_draft' | 'forbidden'
	requiresApproval: boolean
	estimateMethod: ('standard' | 'self')[]

	// Legacy fields for backward compatibility
	stateId?: number
	stateName?: string
	stateDescription?: string
	nextStates?: number[]

	constructor({
		// New API fields
		id,
		name,
		description,
		color,
		icon,
		isInitial,
		isFinal,
		allowEdit,
		editBehavior,
		requiresApproval,
		estimateMethod,
		// Legacy fields
		stateId,
		stateName,
		stateDescription,
		nextStates
	}: Partial<IQuoteState> = {}) {
		// New API fields with legacy support
		this.id = Number(id || stateId || 0)
		this.name = String(name || stateName || '')
		this.description = String(description || stateDescription || '')
		this.color = String(color || '#gray')
		this.icon = String(icon || 'question-circle')
		this.isInitial = Boolean(isInitial || false)
		this.isFinal = Boolean(isFinal || false)
		this.allowEdit = Boolean(allowEdit || false)
		this.editBehavior = editBehavior || 'direct'
		this.requiresApproval = Boolean(requiresApproval || false)
		this.estimateMethod = estimateMethod || ['standard', 'self']

		// Legacy fields for backward compatibility
		this.stateId = this.id
		this.stateName = this.name
		this.stateDescription = this.description
		this.nextStates = nextStates || []

		addToJSON(QuoteState)
	}

	public canTransitionTo(targetStateId: number): boolean {
		return this.nextStates?.includes(targetStateId) || false
	}

	public isEditable(): boolean {
		return this.allowEdit
	}

	public isFinalState(): boolean {
		return this.isFinal
	}

	public getDisplayName(): string {
		return this.name || `State ${this.id}`
	}

	public getDisplayDescription(): string {
		return this.description || this.name || ''
	}

	public getColor(): string {
		return this.color
	}

	public getIcon(): string {
		return this.icon
	}

	public canDirectEdit(): boolean {
		return this.editBehavior === 'direct'
	}

	public requiresResetToDraft(): boolean {
		return this.editBehavior === 'reset_to_draft'
	}

	public isEditForbidden(): boolean {
		return this.editBehavior === 'forbidden'
	}

	public allowsEstimateMethod(method: 'standard' | 'self'): boolean {
		return this.estimateMethod.includes(method)
	}

	public getAllowedEstimateMethods(): ('standard' | 'self')[] {
		return this.estimateMethod
	}
}
